import { useCallback, useEffect, useState } from 'react';
import {
  getDashboardUmbrales,
  saveDashboardUmbrales,
  resetDashboardUmbrales,
} from '../../../api/adminClient';
import {
  DASHBOARD_UMBRALES_DEFAULTS,
  ESTADO_META,
  UMBRALES_FIELD_GROUPS,
  normalizeUmbrales,
} from '../../../utils/dashboardUmbrales';

const ORDEN_EVALUACION = ['sin_evidencia', 'revisar', 'facil', 'dificil', 'buena'];

export default function ConfigDashboardTab() {
  const [form, setForm] = useState({ ...DASHBOARD_UMBRALES_DEFAULTS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const d = await getDashboardUmbrales();
      setForm(normalizeUmbrales(d.umbrales || {}));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value === '' ? '' : Number(value) }));
    setOk('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setOk('');
      const d = await saveDashboardUmbrales(normalizeUmbrales(form));
      setForm(normalizeUmbrales(d.umbrales || {}));
      setOk('Guardado. El Dashboard aplica estos criterios al actualizar.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restablecer los criterios a los valores por defecto?')) return;
    try {
      setSaving(true);
      setError('');
      setOk('');
      const d = await resetDashboardUmbrales();
      setForm(normalizeUmbrales(d.umbrales || {}));
      setOk('Criterios restablecidos.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-8 text-slate-500 text-sm">Cargando…</div>;
  }

  return (
    <div>
      <p className="text-sm text-slate-500">
        Criterios del <span className="font-medium text-slate-700">Estado</span> (semáforo). El{' '}
        <span className="font-medium text-slate-700">Score</span> del listado no se configura acá:
        es un índice 0–100 (uso + completitud + dificultad + evidencia, 25% cada uno) para ordenar.
        Orden de evaluación del Estado:
      </p>
      <div className="flex flex-wrap items-center gap-2 mt-2 mb-6">
        {ORDEN_EVALUACION.map((key, i) => {
          const meta = ESTADO_META[key];
          return (
            <span key={key} className="inline-flex items-center gap-1.5">
              {i > 0 && <span className="text-slate-300 text-xs">→</span>}
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`}>
                {meta.emoji} {meta.label}
              </span>
            </span>
          );
        })}
      </div>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      {ok && <p className="mb-4 text-sm text-emerald-700">{ok}</p>}

      <form onSubmit={handleSave} className="space-y-5">
        {UMBRALES_FIELD_GROUPS.map((group) => (
          <section key={group.title} className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-800">{group.title}</h2>
            {group.help && <p className="text-xs text-slate-400 mt-1 mb-3">{group.help}</p>}
            <div className="space-y-3">
              {group.fields.map((field) => (
                <label key={field.key} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-600 min-w-0">
                    {field.hint && <span className="mr-1.5">{field.hint}</span>}
                    {field.label}
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={field.key === 'min_usos' ? undefined : 100}
                    step={1}
                    value={form[field.key] ?? ''}
                    onChange={(e) => setField(field.key, e.target.value)}
                    className="w-20 shrink-0 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-right focus:outline-none focus:border-violet-500"
                    required
                  />
                </label>
              ))}
            </div>
          </section>
        ))}

        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-50 cursor-pointer"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
          >
            Restablecer defaults
          </button>
        </div>
      </form>
    </div>
  );
}
